import type { FunctionComponent } from "react";
import { useCallback, useState } from "react";
import { t } from "i18next";

import {
  StyledContainer,
  StyledHeader,
  StyledHeaderWrapper,
  StyledLabel,
  StyledDeleteButton,
} from "./styles";
import { Text, Icon } from "@smartsuite/react-ui/lib";
import { Colors, GrayColors } from "@smartsuite/types";

interface ActionGroupProps {
  title: string;
  name: string;
  isError: boolean;
  isEmpty: boolean;
  isActive: boolean;

  children: React.ReactNode;
  onDuplicate?: () => void;
  onDelete: () => void;
  setIsActive: () => void;
  /** styled-components passthrough so contexts other than the root list
   * (e.g. a loop nested in a branch column) can override the container's
   * list-mode geometry (negative margins / max-width). */
  className?: string;
}

export const ActionGroup: FunctionComponent<ActionGroupProps> = ({
  isError = false,
  isEmpty = false,
  isActive,
  title,
  name,
  children,
  setIsActive,
  onDuplicate,
  onDelete,
  className,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  const handleToggle = useCallback(() => {
    setIsCollapsed((prev) => !prev);
  }, []);

  const handleClick = useCallback(() => {
    setIsActive();
  }, []);

  const handleDelete = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    onDelete();
  }, []);

  return (
    <StyledContainer
      className={className}
      isCollapsed={isCollapsed}
      isError={isError}
      isActive={isActive}
      onClick={handleClick}
    >
      <StyledHeader isCollapsed={isCollapsed} onClick={handleToggle}>
        <Icon
          className="action-group-arrow"
          name="arrow-up"
          size="M"
          color="currentColor"
        ></Icon>
        <StyledHeaderWrapper>
          <Text size="xs" weight="medium" className="action-group-title">
            {title}
            {isError && <Icon name="important-lined" color={Colors.bRed} />}
          </Text>
          <Text className="action-group-name" size="l" weight="semibold">
            {name}{" "}
            {isEmpty && (
              <StyledLabel className="action-group-name-empty">
                {t("general.action-group-no-list")}
              </StyledLabel>
            )}
          </Text>
        </StyledHeaderWrapper>
        <StyledDeleteButton onClick={handleDelete}>
          <Icon name="trash-thin" size="S" color={GrayColors["G-8"]} />
        </StyledDeleteButton>
      </StyledHeader>
      {!isCollapsed && children}
    </StyledContainer>
  );
};
